import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Activity, Users, Target, TrendingUp, AlertTriangle, Sparkles, Download,
  FileText, Building2, GraduationCap, Loader2, ChevronRight, RefreshCw
} from 'lucide-react';
import { AppLayout } from '../components/Layout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = sessionStorage.getItem('futurepath_token') || localStorage.getItem('futurepath_token');
  return { Authorization: `Bearer ${t}` };
};

const fmtValue = (fmt, v) => {
  if (v === null || v === undefined) return '—';
  switch (fmt) {
    case 'pct': return `${v}%`;
    case 'delta': return `${v > 0 ? '+' : ''}${v}`;
    case 'score': return Number(v).toFixed(0);
    default: return typeof v === 'number' ? v.toLocaleString() : v;
  }
};
const getPath = (obj, path) => path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj);

const SECTION_ICON = {
  students: Users, readiness: TrendingUp, planning: Target,
  opportunities: Sparkles, risk: AlertTriangle, activity: Activity,
};

function StatCard({ label, value, fmt, target, instrumented }) {
  return (
    <div data-testid={`kpi-card`} className="relative rounded-2xl p-5 bg-[#0e1c33]/80 border border-cyan-500/15
      shadow-[0_0_30px_-12px_rgba(34,211,238,0.5)] hover:shadow-[0_0_40px_-8px_rgba(34,211,238,0.55)]
      transition-shadow duration-300 backdrop-blur">
      <p className="text-sm text-slate-200 font-medium">{label}</p>
      {instrumented === false ? (
        <p className="mt-2 text-sm text-amber-300/90">Not yet instrumented</p>
      ) : (
        <p className="mt-1 text-3xl font-bold text-white tracking-tight">{fmtValue(fmt, value)}</p>
      )}
      {target != null && instrumented !== false && (
        <p className="mt-1 text-xs text-[#39E3DC]">Target {fmtValue(fmt, target)}</p>
      )}
    </div>
  );
}

function FeatureTable({ rows }) {
  if (!rows || !rows.length) return <p className="text-slate-300 text-sm">No feature activity in range yet.</p>;
  return (
    <div data-testid="feature-usage-table" className="rounded-2xl p-5 bg-[#0e1c33]/80 border border-violet-500/20
      shadow-[0_0_30px_-12px_rgba(139,92,246,0.5)] backdrop-blur col-span-full">
      <p className="text-sm text-slate-200 font-medium mb-3">Feature Usage</p>
      <div className="space-y-2">
        {rows.slice(0, 10).map((r) => (
          <div key={r.feature_id} className="flex items-center justify-between text-sm">
            <span className="text-white capitalize">{(r.feature_id || '').replace(/_/g, ' ')}</span>
            <span className="text-[#39E3DC]">{r.users} users · {r.events} events</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AiBlock({ ai }) {
  if (!ai) return null;
  return (
    <div data-testid="ai-activity-block" className="rounded-2xl p-5 bg-[#0e1c33]/80 border border-cyan-500/20
      shadow-[0_0_30px_-12px_rgba(34,211,238,0.5)] backdrop-blur">
      <p className="text-sm text-slate-200 font-medium">AI Recommendation Activity</p>
      <div className="mt-2 flex gap-6">
        <div><p className="text-2xl font-bold text-white">{ai.generated?.toLocaleString?.() ?? 0}</p><p className="text-xs text-slate-300">Generated</p></div>
        <div><p className="text-2xl font-bold text-white">{ai.accepted?.toLocaleString?.() ?? 0}</p><p className="text-xs text-slate-300">Acted on</p></div>
        <div><p className="text-2xl font-bold text-[#39E3DC]">{ai.acceptance_rate ?? 0}%</p><p className="text-xs text-slate-300">Acceptance</p></div>
      </div>
    </div>
  );
}

export default function IntelligencePage() {
  const [filters, setFilters] = useState({ institutions: [], schools: [], cohorts: [] });
  const [sel, setSel] = useState({ institution_id: '', school_id: '', cohort_id: '', tenant_type: '' });
  const [includeDemo, setIncludeDemo] = useState(false);
  const [config, setConfig] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    axios.get(`${API}/intelligence/filters`, { headers: authHeaders() })
      .then((r) => setFilters(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (sel.institution_id) q.set('institution_id', sel.institution_id);
      if (sel.school_id) q.set('school_id', sel.school_id);
      if (sel.cohort_id) q.set('cohort_id', sel.cohort_id);
      if (includeDemo) q.set('include_demo', 'true');
      const cq = new URLSearchParams();
      if (sel.institution_id) cq.set('institution_id', sel.institution_id);
      if (sel.tenant_type) cq.set('tenant_type', sel.tenant_type);
      const [ov, cfg] = await Promise.all([
        axios.get(`${API}/intelligence/overview?${q}`, { headers: authHeaders() }),
        axios.get(`${API}/intelligence/config?${cq}`, { headers: authHeaders() }),
      ]);
      setData(ov.data); setConfig(cfg.data);
    } catch (e) { /* surfaced via empty state */ }
    setLoading(false);
  }, [sel, includeDemo]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const q = new URLSearchParams();
    if (sel.institution_id) q.set('institution_id', sel.institution_id);
    if (sel.school_id) q.set('school_id', sel.school_id);
    if (includeDemo) q.set('include_demo', 'true');
    const r = await axios.get(`${API}/intelligence/export/csv?${q}`, { headers: authHeaders(), responseType: 'blob' });
    const url = URL.createObjectURL(r.data); const a = document.createElement('a');
    a.href = url; a.download = 'futurepath_intelligence.csv'; a.click(); URL.revokeObjectURL(url);
  };
  const exportPdf = async () => {
    const r = await axios.post(`${API}/intelligence/export/pdf`,
      { institution_id: sel.institution_id, school_id: sel.school_id, cohort_id: sel.cohort_id,
        include_demo: includeDemo, title: `${config?.name || 'FuturePath'} Report` },
      { headers: authHeaders(), responseType: 'blob' });
    const url = URL.createObjectURL(r.data); const a = document.createElement('a');
    a.href = url; a.download = 'futurepath_report.pdf'; a.click(); URL.revokeObjectURL(url);
  };

  const openStudent = async (id) => {
    const r = await axios.get(`${API}/intelligence/student/${id}`, { headers: authHeaders() });
    setDrill(r.data);
  };

  const demoMode = data?.scope?.data_mode === 'demo';
  const TenantIcon = config?.tenant_type === 'higher_ed' ? GraduationCap : Building2;

  return (
    <AppLayout>
    <div data-testid="intelligence-page" className="min-h-screen bg-[#0a1628] text-white p-6 lg:p-10 -m-4 lg:-m-8 rounded-2xl">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
        <div>
          <div className="flex items-center gap-3">
            <TenantIcon className="w-7 h-7 text-[#39E3DC]" />
            <h1 className="text-3xl lg:text-4xl font-bold tracking-tight">FuturePath Intelligence</h1>
          </div>
          <p className="text-slate-300 mt-1">{config?.name || 'Executive Overview'} · one engine, configured per institution</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-csv-btn" onClick={exportCsv}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0e1c33] border border-cyan-500/25 text-slate-100 hover:border-cyan-400/50 transition-colors">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button data-testid="export-pdf-btn" onClick={exportPdf}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0e1c33] border border-violet-500/25 text-slate-100 hover:border-violet-400/50 transition-colors">
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {demoMode && (
        <div data-testid="demo-data-banner" className="mb-4 flex items-center gap-2 px-4 py-2 rounded-xl
          bg-red-500/15 border border-red-400/40 text-red-200 font-semibold">
          <AlertTriangle className="w-4 h-4" /> DEMO DATA — includes synthetic accounts. Not production results.
        </div>
      )}

      {/* filters */}
      <div className="flex flex-wrap items-center gap-3 mb-8">
        <select data-testid="institution-filter" value={sel.institution_id}
          onChange={(e) => {
            const inst = filters.institutions.find((i) => i.id === e.target.value);
            setSel({ ...sel, institution_id: e.target.value, school_id: '',
                     tenant_type: inst?.tenant_type || '' });
          }}
          className="px-3 py-2 rounded-xl bg-[#0e1c33] border border-cyan-500/20 text-slate-100 text-sm">
          <option value="">All Institutions</option>
          {filters.institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <select data-testid="school-filter" value={sel.school_id}
          onChange={(e) => setSel({ ...sel, school_id: e.target.value })}
          className="px-3 py-2 rounded-xl bg-[#0e1c33] border border-cyan-500/20 text-slate-100 text-sm">
          <option value="">All Schools / Campuses</option>
          {filters.schools.filter((s) => !sel.institution_id ||
            (s.parent_org_id || s.parent_organization_id) === sel.institution_id)
            .map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select data-testid="cohort-filter" value={sel.cohort_id}
          onChange={(e) => setSel({ ...sel, cohort_id: e.target.value })}
          className="px-3 py-2 rounded-xl bg-[#0e1c33] border border-cyan-500/20 text-slate-100 text-sm">
          <option value="">All Cohorts</option>
          {filters.cohorts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label data-testid="include-demo-toggle" className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0e1c33] border border-slate-600/40 text-sm cursor-pointer">
          <input type="checkbox" checked={includeDemo} onChange={(e) => setIncludeDemo(e.target.checked)}
            className="accent-cyan-400" />
          <span className="text-slate-200">Include Demo Data</span>
        </label>
        <button data-testid="refresh-btn" onClick={load} className="p-2 rounded-xl bg-[#0e1c33] border border-slate-600/40 text-slate-200 hover:text-white">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-slate-300"><Loader2 className="w-5 h-5 animate-spin" /> Loading intelligence…</div>
      ) : !config || !data ? (
        <p className="text-slate-300">No data available.</p>
      ) : (
        <div className="space-y-10">
          {config.sections.map((sec) => {
            const Icon = SECTION_ICON[sec.id] || Activity;
            return (
              <section key={sec.id} data-testid={`section-${sec.id}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Icon className="w-5 h-5 text-[#39E3DC]" />
                  <h2 className="text-lg font-semibold text-white">{sec.title}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {sec.widgets.map((w) => {
                    if (w.fmt === 'feature_table') return <FeatureTable key={w.widget} rows={data.feature_usage} />;
                    if (w.fmt === 'ai_block') return <AiBlock key={w.widget} ai={data.ai} />;
                    return <StatCard key={w.widget} label={w.label} fmt={w.fmt}
                      value={getPath(data, w.metric)} target={w.target} instrumented={w.instrumented} />;
                  })}
                </div>
                {sec.id === 'risk' && data.risk?.sample?.length > 0 && (
                  <div data-testid="intervention-list" className="mt-4 rounded-2xl bg-[#0e1c33]/80 border border-amber-500/20 divide-y divide-slate-700/50">
                    {data.risk.sample.map((s) => (
                      <button key={s.user_id} data-testid={`intervention-row-${s.user_id}`} onClick={() => openStudent(s.user_id)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5 transition-colors">
                        <div>
                          <p className="text-white text-sm font-medium">{s.name}</p>
                          <p className="text-xs text-slate-300">{s.reasons.join(' · ')}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${s.priority === 'critical' ? 'bg-red-500/20 text-red-200' : 'bg-amber-500/20 text-amber-200'}`}>{s.priority}</span>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* student drill-down */}
      {drill && (
        <div data-testid="student-drilldown" onClick={() => setDrill(null)}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 z-50">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-[#0e1c33] border border-cyan-500/25 p-6
            shadow-[0_0_60px_-15px_rgba(34,211,238,0.6)]">
            <h3 className="text-xl font-bold text-white">{drill.student?.name}</h3>
            <p className="text-sm text-slate-300 mb-4">Grade {drill.student?.grade || '—'} · {drill.student?.career_goal || 'No career goal'}</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl bg-[#0a1628] p-3"><p className="text-slate-300">Profile</p><p className="text-white text-lg font-semibold">{drill.student?.profile_completion}%</p></div>
              <div className="rounded-xl bg-[#0a1628] p-3"><p className="text-slate-300">Readiness</p><p className="text-white text-lg font-semibold">{drill.readiness?.current ?? '—'}</p></div>
              <div className="rounded-xl bg-[#0a1628] p-3"><p className="text-slate-300">Milestones</p><p className="text-white text-lg font-semibold">{drill.current_state?.milestones_completed || 0}</p></div>
              <div className="rounded-xl bg-[#0a1628] p-3"><p className="text-slate-300">Opps Applied</p><p className="text-white text-lg font-semibold">{drill.current_state?.opportunities_applied || 0}</p></div>
            </div>
            {drill.recent_events?.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-slate-200 mb-2">Recent activity</p>
                <div className="space-y-1 max-h-40 overflow-auto">
                  {drill.recent_events.slice(0, 10).map((e) => (
                    <div key={e.event_id} className="flex justify-between text-xs text-slate-300">
                      <span className="text-[#39E3DC]">{e.event_name}</span>
                      <span>{(e.occurred_at || '').slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button onClick={() => setDrill(null)} className="mt-5 w-full py-2 rounded-xl bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30 transition-colors">Close</button>
          </div>
        </div>
      )}
    </div>
    </AppLayout>
  );
}
